import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Calendar, User, Tag, Share2, Facebook, Linkedin, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Extra photos beyond the cover image (images[0] is already shown as the featured image above).
  const galleryImages = (post?.images || []).length > 1 ? post.images.slice(1) : [];

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
        
        // Fetch related posts
        const relatedRes = await axios.get(`${API}/blog?category=${response.data.category}&limit=4`);
        setRelatedPosts(relatedRes.data.filter(p => p.slug !== slug).slice(0, 3));
      } catch (error) {
        console.error("Failed to fetch post:", error);
        toast.error("Blog post not found");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowRight") setLightboxIndex((i) => (i + 1) % galleryImages.length);
      if (e.key === "ArrowLeft") setLightboxIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxIndex, galleryImages.length]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const sharePost = (platform) => {
    const url = window.location.href;
    const title = post?.title || "";
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`
    };

    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="pt-16 md:pt-20 lg:pt-24 min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="h-8 w-32 bg-muted animate-pulse rounded mb-4" />
          <div className="h-12 w-3/4 bg-muted animate-pulse rounded mb-8" />
          <div className="aspect-video bg-muted animate-pulse rounded-2xl mb-8" />
          <div className="space-y-4">
            <div className="h-4 w-full bg-muted animate-pulse rounded" />
            <div className="h-4 w-full bg-muted animate-pulse rounded" />
            <div className="h-4 w-2/3 bg-muted animate-pulse rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-16 md:pt-20 lg:pt-24 min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-praroha-forest mb-4">Blog Post Not Found</h1>
          <Link to="/blog">
            <Button variant="outline">Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-20 lg:pt-24" data-testid="blog-post-page">
      {/* Back Link */}
      <div className="bg-praroha-cream/50 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-praroha-forest transition-colors"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>
        </div>
      </div>

      {/* Article */}
      <article className="py-12 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-praroha-sage/10 text-praroha-sage rounded-full">
                <Tag className="w-3 h-3" />
                {post.category}
              </span>
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
              <span className="inline-flex items-center gap-1">
                <User className="w-4 h-4" />
                {post.author}
              </span>
            </div>

            {/* Title */}
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-praroha-forest mb-6" data-testid="blog-post-title">
              {post.title}
            </h1>

            {/* Featured Image */}
            <div className="aspect-video overflow-hidden rounded-2xl mb-8">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover"
                data-testid="blog-post-image"
              />
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none mb-8">
              <p className="text-lg text-muted-foreground leading-relaxed">
                {post.excerpt}
              </p>
              <div className="mt-6 text-muted-foreground leading-relaxed">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({node, ...props}) => <h1 className="font-heading text-2xl font-bold text-praroha-forest mt-6 mb-3" {...props} />,
                    h2: ({node, ...props}) => <h2 className="font-heading text-xl font-bold text-praroha-forest mt-8 mb-3" {...props} />,
                    h3: ({node, ...props}) => <h3 className="font-heading text-lg font-semibold text-praroha-forest mt-4 mb-2" {...props} />,
                    p: ({node, ...props}) => <p className="my-4" {...props} />,
                    strong: ({node, ...props}) => <strong className="font-semibold text-foreground" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc pl-5 space-y-1 my-3" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal pl-5 space-y-1 my-3" {...props} />,
                    table: ({node, ...props}) => <table className="w-full border-collapse my-4" {...props} />,
                    th: ({node, ...props}) => <th className="border border-border/50 bg-praroha-cream px-3 py-2 text-left text-sm font-semibold text-praroha-forest" {...props} />,
                    td: ({node, ...props}) => <td className="border border-border/50 px-3 py-2 text-sm" {...props} />,
                    blockquote: ({node, ...props}) => (
                      <div className="my-6 px-5 py-4 bg-praroha-sage/10 border-l-4 border-praroha-sage rounded-r-lg" {...props} />
                    ),
                    img: ({node, ...props}) => (
                      <img className="w-full max-w-xl mx-auto rounded-2xl my-6" loading="lazy" {...props} />
                    ),
                  }}
                >
                  {post.content}
                </ReactMarkdown>
              </div>
            </div>

            {/* Gallery */}
          {/*  {galleryImages.length > 0 && (
              <div className="mb-8" data-testid="blog-post-gallery">
                <h2 className="font-heading text-xl font-semibold text-praroha-forest mb-4">
                  Gallery
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {galleryImages.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setLightboxIndex(idx)}
                      className="aspect-square overflow-hidden rounded-xl group"
                      data-testid={`gallery-thumb-${idx}`}
                    >
                      <img
                        src={img}
                        alt={`${post.title} photo ${idx + 2}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}**/}

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-praroha-cream text-praroha-forest text-sm rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Share */}
            <div className="flex items-center gap-4 py-6 border-t border-b border-border/50">
              <span className="text-muted-foreground flex items-center gap-2">
                <Share2 className="w-4 h-4" />
                Share this article:
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => sharePost('facebook')}
                  className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors"
                  data-testid="share-facebook"
                >
                  <Facebook className="w-4 h-4" />
                </button>
                <button
                  onClick={() => sharePost('linkedin')}
                  className="w-9 h-9 rounded-full bg-blue-700 text-white flex items-center justify-center hover:bg-blue-800 transition-colors"
                  data-testid="share-linkedin"
                >
                  <Linkedin className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </article>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center px-4"
            onClick={() => setLightboxIndex(null)}
            data-testid="gallery-lightbox"
          >
            <button
              type="button"
              onClick={() => setLightboxIndex(null)}
              className="absolute top-4 right-4 text-white/80 hover:text-white"
              data-testid="gallery-lightbox-close"
            >
              <X className="w-7 h-7" />
            </button>

            {galleryImages.length > 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length); }}
                className="absolute left-4 text-white/80 hover:text-white"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
            )}

            <motion.img
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              src={galleryImages[lightboxIndex]}
              alt={`${post.title} photo ${lightboxIndex + 2}`}
              className="max-h-[85vh] max-w-full rounded-lg object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {galleryImages.length > 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i + 1) % galleryImages.length); }}
                className="absolute right-4 text-white/80 hover:text-white"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-gradient-to-b from-background to-praroha-cream/30" data-testid="related-posts">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-praroha-forest mb-8">
              Related Articles
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link key={relatedPost.id} to={`/blog/${relatedPost.slug}`}>
                  <Card className="group h-full overflow-hidden border-border/40 hover:shadow-hover transition-all duration-500">
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-4">
                      <span className="text-xs text-praroha-terracotta">{relatedPost.category}</span>
                      <h3 className="font-heading text-lg font-semibold text-praroha-forest mt-1 group-hover:text-praroha-sage transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
